import React from 'react';
import NewStaffBadge from '../../components/NewStaffBadge';
import {
  CalendarDays,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Globe,
  UserRound,
} from 'lucide-react';

type StaffAccordionCardSnsLink = {
  platform: string;
  url: string;
};

type StaffAccordionCardProps = {
  isNew?: boolean;
  name: string;
  roleName?: string | null;
  bio?: string | null;
  iconUrl?: string | null;
  snsLinks?: StaffAccordionCardSnsLink[];
  isExpanded: boolean;
  onToggle: () => void;
  onAction: () => void;
  actionLabel: string;
  actionEyebrow?: string;
  toneIndex?: number;
};

const InstagramIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className={className}>
    <rect x="3.25" y="3.25" width="17.5" height="17.5" rx="5.25" stroke="currentColor" strokeWidth="1.8" />
    <circle cx="12" cy="12" r="4.2" stroke="currentColor" strokeWidth="1.8" />
    <circle cx="17.2" cy="6.8" r="1.15" fill="currentColor" />
  </svg>
);

const TikTokIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className={className}>
    <path
      fill="currentColor"
      d="M14.88 3.5c.28 1.82 1.25 3.3 3.14 4.07.56.23 1.16.35 1.79.37v2.8a8.52 8.52 0 0 1-3.77-.93v5.09a5.92 5.92 0 1 1-5.09-5.86v2.86a3.11 3.11 0 1 0 2.12 2.95V3.5h1.81Z"
    />
  </svg>
);

const XIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className={className}>
    <path
      fill="currentColor"
      d="M18.91 3.75h2.54l-5.56 6.35L22.43 20h-5.12l-4-5.23L8.73 20H6.18l5.94-6.79L1.87 3.75h5.25l3.62 4.77 4.17-4.77Zm-.9 14.72h1.41L6.36 5.2H4.84l13.17 13.27Z"
    />
  </svg>
);

const YoutubeIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className={className}>
    <path
      fill="currentColor"
      d="M21.07 7.18a2.9 2.9 0 0 0-2.03-2.06C17.26 4.62 12 4.62 12 4.62s-5.26 0-7.04.5A2.9 2.9 0 0 0 2.93 7.18c-.5 1.8-.5 4.82-.5 4.82s0 3.02.5 4.82a2.9 2.9 0 0 0 2.03 2.06c1.78.5 7.04.5 7.04.5s5.26 0 7.04-.5a2.9 2.9 0 0 0 2.03-2.06c.5-1.8.5-4.82.5-4.82s0-3.02-.5-4.82ZM10.28 15.2V8.8l5.62 3.2-5.62 3.2Z"
    />
  </svg>
);

const FacebookIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className={className}>
    <path
      fill="currentColor"
      d="M13.39 20.5v-6.62h2.22l.33-2.58h-2.55V9.66c0-.75.2-1.26 1.28-1.26h1.37V6.09c-.24-.03-1.05-.09-2-.09-1.98 0-3.33 1.2-3.33 3.42v1.88H8.5v2.58h2.2v6.62h2.69Z"
    />
  </svg>
);

const LineIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className={className}>
    <path
      fill="currentColor"
      d="M12 4.1c-4.85 0-8.8 3.16-8.8 7.04 0 3.48 3.12 6.4 7.33 6.95.29.06.69.18.79.42.09.22.06.56.03.78l-.13.75c-.04.22-.18.87.76.48.95-.4 5.14-3.03 7.01-5.18 1.29-1.4 1.91-2.81 1.91-4.2 0-3.88-3.95-7.04-8.9-7.04Zm-4.56 8.87a.5.5 0 0 1-.5-.5V9.56a.5.5 0 0 1 1 0v2.41h1.33a.5.5 0 0 1 0 1H7.44Zm3.27 0a.5.5 0 0 1-.5-.5V9.56a.5.5 0 0 1 1 0v2.91a.5.5 0 0 1-.5.5Zm4.26 0a.5.5 0 0 1-.4-.2l-1.62-2.18v1.88a.5.5 0 0 1-1 0V9.56a.5.5 0 0 1 .9-.3l1.62 2.18V9.56a.5.5 0 0 1 1 0v2.91a.5.5 0 0 1-.5.5Zm2.88-1h1.33a.5.5 0 0 1 0 1h-1.83a.5.5 0 0 1-.5-.5V9.56a.5.5 0 0 1 .5-.5h1.83a.5.5 0 0 1 0 1h-1.33v.45h1.33a.5.5 0 0 1 0 1h-1.33v.46Z"
    />
  </svg>
);

const SNS_LABEL_MAP: Record<string, string> = {
  instagram: 'Instagram',
  tiktok: 'TikTok',
  x: 'X',
  youtube: 'YouTube',
  facebook: 'Facebook',
  line: 'LINE',
  website: 'Website',
  other: 'SNS',
};

const STAFF_CARD_TONES = [
  {
    collapsed: 'border-[#dfeee6] bg-[linear-gradient(135deg,rgba(255,255,255,1),rgba(240,250,245,0.96))]',
    expanded: 'border-[#8ee2bf] bg-[linear-gradient(180deg,rgba(255,255,255,1),rgba(232,252,243,0.94))] shadow-[0_24px_56px_rgba(16,185,129,0.12)]',
    accent: 'bg-emerald-100 text-emerald-700',
    avatar: 'border-emerald-200 bg-[radial-gradient(circle_at_top,#e5fff3_0%,#f8fafc_78%)]',
  },
  {
    collapsed: 'border-[#e6e4f8] bg-[linear-gradient(135deg,rgba(255,255,255,1),rgba(245,243,255,0.96))]',
    expanded: 'border-[#c5b8ff] bg-[linear-gradient(180deg,rgba(255,255,255,1),rgba(243,240,255,0.96))] shadow-[0_24px_56px_rgba(139,92,246,0.10)]',
    accent: 'bg-violet-100 text-violet-700',
    avatar: 'border-violet-200 bg-[radial-gradient(circle_at_top,#f2ecff_0%,#f8fafc_78%)]',
  },
  {
    collapsed: 'border-[#f5e0df] bg-[linear-gradient(135deg,rgba(255,255,255,1),rgba(255,244,242,0.96))]',
    expanded: 'border-[#ffc0bb] bg-[linear-gradient(180deg,rgba(255,255,255,1),rgba(255,241,239,0.96))] shadow-[0_24px_56px_rgba(244,114,182,0.10)]',
    accent: 'bg-rose-100 text-rose-700',
    avatar: 'border-rose-200 bg-[radial-gradient(circle_at_top,#fff0f0_0%,#f8fafc_78%)]',
  },
] as const;

const SNS_STYLE_MAP: Record<string, { icon: React.FC<{ className?: string }>; chipClass: string; iconWrapClass: string }> = {
  instagram: {
    icon: InstagramIcon,
    chipClass: 'border-rose-200 bg-gradient-to-r from-fuchsia-600 via-rose-500 to-orange-400 text-white hover:opacity-95',
    iconWrapClass: 'bg-white/18 text-white',
  },
  tiktok: {
    icon: TikTokIcon,
    chipClass: 'border-slate-900 bg-[#111111] text-white hover:bg-[#1a1a1a]',
    iconWrapClass: 'bg-white/12 text-[#63fff3]',
  },
  x: {
    icon: XIcon,
    chipClass: 'border-slate-900 bg-black text-white hover:bg-slate-900',
    iconWrapClass: 'bg-white/12 text-white',
  },
  youtube: {
    icon: YoutubeIcon,
    chipClass: 'border-red-300 bg-[#ff0033] text-white hover:bg-[#e2002e]',
    iconWrapClass: 'bg-white/16 text-white',
  },
  facebook: {
    icon: FacebookIcon,
    chipClass: 'border-blue-300 bg-[#1877f2] text-white hover:bg-[#1468d4]',
    iconWrapClass: 'bg-white/16 text-white',
  },
  line: {
    icon: LineIcon,
    chipClass: 'border-emerald-300 bg-[#06c755] text-white hover:bg-[#05b44d]',
    iconWrapClass: 'bg-white/16 text-white',
  },
  website: {
    icon: Globe,
    chipClass: 'border-cyan-300 bg-cyan-500 text-white hover:bg-cyan-600',
    iconWrapClass: 'bg-white/16 text-white',
  },
  other: {
    icon: Globe,
    chipClass: 'border-amber-300 bg-amber-500 text-white hover:bg-amber-600',
    iconWrapClass: 'bg-white/16 text-white',
  },
};

const StaffAccordionCard: React.FC<StaffAccordionCardProps> = ({
  name,
  roleName,
  bio,
  iconUrl,
  snsLinks = [],
  isExpanded,
  onToggle,
  onAction,
  actionLabel,
  actionEyebrow = 'Staff booking',
  toneIndex = 0,
  isNew = false,
}) => {
  const tone = STAFF_CARD_TONES[toneIndex % STAFF_CARD_TONES.length];
  const visibleSnsLinks = snsLinks.filter((link) => Boolean(link.url?.trim()));
  const displayName = name?.trim() || 'Staff';
  const displayRoleName = roleName?.trim() || 'Staff';

  return (
    <section
      className={[
        'relative overflow-hidden rounded-[28px] border shadow-[0_16px_38px_rgba(15,23,42,0.08)] transition-all duration-300',
        isExpanded ? tone.expanded : tone.collapsed,
      ].join(' ')}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-5 top-0 h-12 rounded-b-[24px] bg-[linear-gradient(180deg,rgba(255,255,255,0.78),rgba(255,255,255,0.08))]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-[linear-gradient(180deg,rgba(255,255,255,0),rgba(255,255,255,0.16))]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-12 top-5 h-28 w-28 rounded-full bg-white/40 blur-2xl"
      />

      <button
        type="button"
        onClick={onToggle}
        className={[
          'relative w-full px-5 py-5 text-left transition',
          isExpanded ? 'bg-transparent' : 'hover:bg-slate-50/60',
        ].join(' ')}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 items-center gap-4">
            <div
              className={[
                'flex h-[88px] w-[88px] items-center justify-center overflow-hidden rounded-[28px] border shadow-sm',
                tone.avatar,
              ].join(' ')}
            >
              {iconUrl ? (
                <img
                  src={iconUrl}
                  alt={displayName}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center">
                  <UserRound className="h-9 w-9 text-slate-400" />
                </div>
              )}
            </div>

            <div className="min-w-0 flex-1">
              {isNew ? <NewStaffBadge /> : null}
              <div className="truncate text-[28px] font-semibold tracking-[-0.03em] text-slate-950">
                {displayName}
              </div>
              <span
                className={[
                  'mt-3 inline-flex rounded-full px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em]',
                  isExpanded ? tone.accent : 'bg-white/70 text-slate-600',
                ].join(' ')}
              >
                {displayRoleName}
              </span>
            </div>
          </div>

          <div
            className={[
              'shrink-0 rounded-full border border-white/60 p-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.78)] transition-colors',
              isExpanded ? tone.accent : 'bg-white/70 text-slate-500',
            ].join(' ')}
          >
            <ChevronDown
              className={[
                'h-5 w-5 transition-transform duration-300',
                isExpanded ? 'rotate-180' : '',
              ].join(' ')}
            />
          </div>
        </div>
      </button>

      <div
        className={[
          'grid transition-[grid-template-rows] duration-300 ease-out',
          isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        ].join(' ')}
      >
        <div className="overflow-hidden">
          <div className="border-t border-slate-100 px-5 pb-5 pt-1">
            <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-start">
              <div className="space-y-3">
                <button
                  type="button"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onAction();
                  }}
                  className="flex w-full items-center justify-between gap-3 rounded-[22px] border border-emerald-200 bg-[linear-gradient(135deg,#12c2b8,#0aa59f)] px-4 py-3.5 text-left text-white shadow-[0_16px_30px_rgba(10,186,181,0.22)] transition hover:brightness-[1.03]"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[14px] bg-white/16">
                      <CalendarDays className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/75">
                        {actionEyebrow}
                      </div>
                      <div className="truncate text-[15px] font-semibold">
                        {actionLabel}
                      </div>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 shrink-0 text-white/90" />
                </button>

                <div className="relative overflow-hidden rounded-[24px] border border-white/80 bg-white/90 px-4 py-4 shadow-sm">
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-x-4 top-0 h-8 rounded-b-[18px] bg-[linear-gradient(180deg,rgba(255,255,255,0.82),rgba(255,255,255,0.02))]"
                  />
                  <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                    Profile
                  </div>
                  <p className="mt-2 whitespace-pre-line break-words text-sm leading-6 text-slate-600">
                    {bio?.trim() || 'No profile available yet.'}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 md:max-w-[320px] md:justify-end">
                {visibleSnsLinks.length > 0 ? (
                  visibleSnsLinks.map((link, index) => {
                    const normalizedPlatform =
                      link.platform?.trim().toLowerCase() || 'other';
                    const label =
                      SNS_LABEL_MAP[normalizedPlatform] ?? SNS_LABEL_MAP.other;
                    const snsStyle =
                      SNS_STYLE_MAP[normalizedPlatform] ?? SNS_STYLE_MAP.other;
                    const Icon = snsStyle.icon;

                    return (
                      <button
                        key={`${link.platform}-${index}`}
                        type="button"
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          window.open(link.url, '_blank', 'noopener,noreferrer');
                        }}
                        className={[
                          'inline-flex items-center gap-3 rounded-[18px] border px-4 py-3 text-sm font-semibold shadow-sm transition',
                          snsStyle.chipClass,
                        ].join(' ')}
                      >
                        <span
                          className={[
                            'flex h-8 w-8 items-center justify-center rounded-full',
                            snsStyle.iconWrapClass,
                          ].join(' ')}
                        >
                          <Icon className="h-[18px] w-[18px]" />
                        </span>
                        <span>{label}</span>
                        <ExternalLink className="h-4 w-4 opacity-85" />
                      </button>
                    );
                  })
                ) : (
                  <div className="rounded-[18px] border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-400">
                    No SNS links
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default StaffAccordionCard;
